import { lazy } from 'react';
import type { RouteComponent } from '@tanstack/react-router';

export interface SinkComponent {
  slug: string;
  name: string;
  description: string;
  component: RouteComponent;
}

/**
 * Single source of truth for all sink component definitions.
 * Used by both the routes and navigation.
 */
export const sinkComponents: SinkComponent[] = [
  {
    slug: 'button',
    name: 'Button',
    description: 'Interactive button elements with variants and sizes',
    component: lazy(() => import('@/views/sink/pages/ButtonPage')),
  },
  {
    slug: 'card',
    name: 'Card',
    description: 'Container components for grouping related content',
    component: lazy(() => import('@/views/sink/pages/CardPage')),
  },
  {
    slug: 'input',
    name: 'Input',
    description: 'Text input fields with various states',
    component: lazy(() => import('@/views/sink/pages/InputPage')),
  },
  {
    slug: 'label',
    name: 'Label',
    description: 'Labels for form inputs',
    component: lazy(() => import('@/views/sink/pages/LabelPage')),
  },
  {
    slug: 'field',
    name: 'Field',
    description: 'Combined label, input, and error components',
    component: lazy(() => import('@/views/sink/pages/FieldPage')),
  },
  {
    slug: 'checkbox',
    name: 'Checkbox',
    description: 'Boolean selection controls',
    component: lazy(() => import('@/views/sink/pages/CheckboxPage')),
  },
  {
    slug: 'radio-group',
    name: 'Radio Group',
    description: 'Single-choice selection groups',
    component: lazy(() => import('@/views/sink/pages/RadioGroupPage')),
  },
  {
    slug: 'native-select',
    name: 'Native Select',
    description: 'Browser native dropdown selections',
    component: lazy(() => import('@/views/sink/pages/NativeSelectPage')),
  },
  {
    slug: 'input-otp',
    name: 'Input OTP',
    description: 'One-time password input fields',
    component: lazy(() => import('@/views/sink/pages/InputOtpPage')),
  },
  {
    slug: 'calendar',
    name: 'Calendar',
    description: 'Date picker components',
    component: lazy(() => import('@/views/sink/pages/CalendarPage')),
  },
  {
    slug: 'popover',
    name: 'Popover',
    description: 'Floating content containers',
    component: lazy(() => import('@/views/sink/pages/PopoverPage')),
  },
  {
    slug: 'dialog',
    name: 'Dialog',
    description: 'Modal dialog windows',
    component: lazy(() => import('@/views/sink/pages/DialogPage')),
  },
  {
    slug: 'command',
    name: 'Command',
    description: 'Command palette with search',
    component: lazy(() => import('@/views/sink/pages/CommandPage')),
  },
  {
    slug: 'carousel',
    name: 'Carousel',
    description: 'Scrollable content sliders',
    component: lazy(() => import('@/views/sink/pages/CarouselPage')),
  },
  {
    slug: 'separator',
    name: 'Separator',
    description: 'Visual dividers between content',
    component: lazy(() => import('@/views/sink/pages/SeparatorPage')),
  },
  {
    slug: 'skeleton',
    name: 'Skeleton',
    description: 'Loading placeholder elements',
    component: lazy(() => import('@/views/sink/pages/SkeletonPage')),
  },
  {
    slug: 'toast',
    name: 'Toast (Sonner)',
    description: 'Toast notification messages',
    component: lazy(() => import('@/views/sink/pages/ToastPage')),
  },
  {
    slug: 'prior-auth-status',
    name: 'Prior Auth Status',
    description: 'Status cards for prior authorization workflows',
    component: lazy(() => import('@/views/sink/pages/PriorAuthStatusPage')),
  },
];

/** Navigation items derived from sink components */
export const sinkNavItems = sinkComponents.map((c) => ({
  path: `/sink/${c.slug}`,
  name: c.name,
}));
